// The rule that a work report booked against an earlier day has to say why.
//
// Kept out of the component so it can be driven directly by
// `scripts/verify-backdated-work-report.mjs`, and so the modal opened from an ordinary Request
// and the one opened from a sub-task card cannot answer the question differently — they are the
// same modal, and this is the same rule.
//
// The modal is only the first of three doors: the same entry is reachable from the API and from
// the update endpoint, both of which enforce this server-side. What lives here is the part that
// stops somebody submitting a form they would only be refused for.

// The exact sentence the product requires, and the exact sentence the API returns. One
// constant, one wording — a person refused by the API must read what the form would have said.
export const BACKDATED_WORK_REPORT_COMMENT_MESSAGE =
    'Selecting a date for the work report before today requires an explanation note in the Comment box before submission.';

// The entry's `date_at` is sent as a UTC calendar date, and the server compares it against today
// in UTC. Reading a local calendar date here instead would let the form enable Create on a
// request the API then refuses — a form that lies about what will be accepted.
export const resolveUtcCalendarDate = (date: Date): string => date.toISOString().slice(0, 10);

// Whether the selected work date falls before today.
//
// No selected date is not a backdating problem: Create is already blocked while the date is
// empty, and claiming a second reason would show a notice about a date nobody has picked.
export const isWorkDateBackdated = (workDate: Date | null, now: Date): boolean => {
    if (workDate === null) {
        return false;
    }

    const workDateUtc = resolveUtcCalendarDate(workDate);
    const todayUtc = resolveUtcCalendarDate(now);

    return workDateUtc < todayUtc;
};

// Whether a backdated entry is still missing its explanation.
//
// Whitespace is empty. A single space satisfies "not an empty string" while explaining nothing
// at all, and it is the cheapest possible way past this rule.
export const isBackdatedExplanationMissing = (workDate: Date | null, comment: string, now: Date): boolean =>
    isWorkDateBackdated(workDate, now) && comment.trim() === '';
